/**
 * Security helpers for handling values that originate from Firebase (or any
 * other source the renderer can't fully trust). Apply these at every render
 * site that accepts user-controlled input.
 */

/**
 * Allow-list for URL schemes that may flow into <Link href>, <a href>, or
 * any other navigation-style attribute. `javascript:`, `data:`, `vbscript:`,
 * etc. are rejected because they execute script when clicked.
 *
 * Returns the original URL if the scheme (or relative form) is permitted,
 * otherwise the empty `#` fragment so the link renders but navigates
 * nowhere.
 *
 * Use at every Firebase-sourced href call site, e.g.
 *   <Link href={safeUrl(subsidiary.websiteUrl)}>…</Link>
 */
export function safeUrl(url: string | null | undefined): string {
  if (!url) return "#";

  const trimmed = url.trim();
  if (trimmed === "") return "#";

  // A protocol-relative URL looks root-relative but leaves the site, and
  // browsers read "/\" the same as "//". Reject both, matching the rich-text
  // sanitiser's allowProtocolRelative: false, so the two guards agree.
  if (trimmed.startsWith("//") || trimmed.startsWith("/\\")) return "#";

  // Relative paths (anchors, query strings, root-relative) are always safe
  if (
    trimmed.startsWith("/") ||
    trimmed.startsWith("#") ||
    trimmed.startsWith("?")
  ) {
    return trimmed;
  }

  // Allow only the safe absolute schemes
  const lower = trimmed.toLowerCase();
  if (
    lower.startsWith("https://") ||
    lower.startsWith("http://") ||
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:")
  ) {
    return trimmed;
  }

  return "#";
}
